import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Card } from 'react-bootstrap';
import firebase from '../firebaseConfig';
import '../styles/DashboardSettings.css';

const DashboardSettings = ({ show, onHide, user, currentConfig, onConfigChange }) => {
    const [config, setConfig] = useState(currentConfig);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setConfig(currentConfig);
    }, [currentConfig]);

    const handleSectionToggle = (sectionKey) => {
        setConfig(prev => ({
            ...prev,
            sections: {
                ...prev.sections,
                [sectionKey]: {
                    ...prev.sections[sectionKey],
                    enabled: !prev.sections[sectionKey].enabled
                }
            }
        }));
    };

    const handleCardToggle = (sectionKey, cardKey) => {
        setConfig(prev => ({
            ...prev,
            sections: {
                ...prev.sections,
                [sectionKey]: {
                    ...prev.sections[sectionKey],
                    cards: {
                        ...prev.sections[sectionKey].cards,
                        [cardKey]: {
                            ...prev.sections[sectionKey].cards[cardKey],
                            enabled: !prev.sections[sectionKey].cards[cardKey].enabled
                        }
                    }
                }
            }
        }));
    };

    const handleButtonToggle = (buttonKey) => {
        setConfig(prev => ({
            ...prev,
            sections: {
                ...prev.sections,
                quickAccess: {
                    ...prev.sections.quickAccess,
                    buttons: {
                        ...prev.sections.quickAccess.buttons,
                        [buttonKey]: {
                            ...prev.sections.quickAccess.buttons[buttonKey],
                            enabled: !prev.sections.quickAccess.buttons[buttonKey].enabled
                        }
                    }
                }
            }
        }));
    };

    const handleChartToggle = (chartKey) => {
        setConfig(prev => ({
            ...prev,
            sections: {
                ...prev.sections,
                charts: {
                    ...prev.sections.charts,
                    charts: {
                        ...prev.sections.charts.charts,
                        [chartKey]: {
                            ...prev.sections.charts.charts[chartKey],
                            enabled: !prev.sections.charts.charts[chartKey].enabled
                        }
                    }
                }
            }
        }));
    };

    const handleComponentToggle = (componentKey) => {
        setConfig(prev => ({
            ...prev,
            sections: {
                ...prev.sections,
                activities: {
                    ...prev.sections.activities,
                    components: {
                        ...prev.sections.activities.components,
                        [componentKey]: {
                            ...prev.sections.activities.components[componentKey],
                            enabled: !prev.sections.activities.components[componentKey].enabled
                        }
                    }
                }
            }
        }));
    };

    const handleOrderChange = (sectionKey, newOrder) => {
        setConfig(prev => ({
            ...prev,
            sections: {
                ...prev.sections,
                [sectionKey]: {
                    ...prev.sections[sectionKey],
                    order: parseInt(newOrder)
                }
            }
        }));
    };

    const saveConfiguration = async () => {
        setSaving(true);
        try {
            if (user) {
                const userRef = firebase.firestore().collection('users').doc(user.uid);
                await userRef.update({
                    dashboardConfig: config
                });
                onConfigChange(config);
                onHide();
            }
        } catch (error) {
            console.error('Error saving dashboard configuration:', error);
        }
        setSaving(false);
    };

    const resetToDefault = () => {
        const defaultConfig = {
            sections: {
                statsCards: {
                    enabled: true,
                    order: 1,
                    cards: {
                        flowTime: { enabled: true, order: 1 },
                        averageScore: { enabled: true, order: 2 },
                        goals: { enabled: true, order: 3 },
                        experiences: { enabled: true, order: 4 }
                    }
                },
                
                charts: {
                    enabled: true,
                    order: 2,
                    charts: {
                        flowEvolution: { enabled: true, order: 1 },
                        categoryDistribution: { enabled: true, order: 2 }
                    }
                },
                focusTimer: {
                    enabled: true,
                    order: 3
                },
                quickAccess: {
                    enabled: true,
                    order: 4,
                    buttons: {
                        dashboard: { enabled: true, order: 1 },
                        flowtracker: { enabled: true, order: 2 },
                        cycles: { enabled: true, order: 3 },
                        experiences: { enabled: true, order: 4 },
                        positiveAffirmations: { enabled: true, order: 5 },
                        tutorials: { enabled: true, order: 6 },
                        personalTraining: { enabled: true, order: 7 },
                        feedback: { enabled: true, order: 8 }
                    }
                },
                activities: {
                    enabled: true,
                    order: 5,
                    components: {
                        recentActivities: { enabled: true, order: 1 },
                        upcomingDeadlines: { enabled: true, order: 2 }
                    }
                }
            }
        };
        setConfig(defaultConfig);
    };

    const sectionLabels = {
        statsCards: 'Statistics Cards',
        focusTimer: 'Focus Timer',
        charts: 'Charts',
        quickAccess: 'Quick Access',
        activities: 'Activities'
    };

    const cardLabels = {
        flowTime: 'Flow Time',
        averageScore: 'Average Score',
        goals: 'Goals',
        experiences: 'Experiences'
    };

    const chartLabels = {
        flowEvolution: 'Flow Evolution',
        categoryDistribution: 'Category Distribution'
    };

    const buttonLabels = {
        dashboard: 'Flow State Habits',
        flowtracker: 'Flow Tracker',
        cycles: 'Cycles',
        experiences: 'Experiences',
        positiveAffirmations: 'Affirmations & Journal',
        tutorials: 'Tutorials',
        personalTraining: 'Personal Training',
        feedback: 'Feedback'
    };

    const componentLabels = {
        recentActivities: 'Recent Activities',
        upcomingDeadlines: 'Upcoming Deadlines'
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    <i className="fas fa-cog me-2"></i>
                    Dashboard Settings
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="dashboard-settings-body">
                <p className="text-muted mb-4">
                    Customize your dashboard by enabling/disabling sections and changing their order.
                </p>

                {/* Sections principales */}
                {Object.entries(config.sections).map(([sectionKey, sectionConfig]) => (
                    <Card key={sectionKey} className="mb-3">
                        <Card.Header>
                            <Row className="align-items-center">
                                <Col>
                                    <Form.Check
                                        type="switch"
                                        id={`section-${sectionKey}`}
                                        label={sectionLabels[sectionKey]}
                                        checked={sectionConfig.enabled}
                                        onChange={() => handleSectionToggle(sectionKey)}
                                        className="section-toggle"
                                    />
                                </Col>
                                <Col xs="auto">
                                    <Form.Group className="mb-0">
                                        <Form.Label className="small text-muted">Order:</Form.Label>
                                        <Form.Control
                                            type="number"
                                            size="sm"
                                            style={{ width: '70px' }}
                                            value={sectionConfig.order || 1}
                                            onChange={(e) => handleOrderChange(sectionKey, e.target.value)}
                                            disabled={!sectionConfig.enabled}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Card.Header>
                        
                        {sectionConfig.enabled && (
                            <Card.Body>
                                {/* Cartes statistiques */}
                                {sectionKey === 'statsCards' && sectionConfig.cards && (
                                    <div>
                                        <h6 className="text-muted mb-3">Statistics Cards:</h6>
                                        <Row>
                                            {Object.entries(sectionConfig.cards).map(([cardKey, cardConfig]) => (
                                                <Col md={6} key={cardKey} className="mb-2">
                                                    <Form.Check
                                                        type="checkbox"
                                                        id={`card-${cardKey}`}
                                                        label={cardLabels[cardKey]}
                                                        checked={cardConfig.enabled}
                                                        onChange={() => handleCardToggle('statsCards', cardKey)}
                                                    />
                                                </Col>
                                            ))}
                                        </Row>
                                    </div>
                                )}

                                {/* Graphiques */}
                                {sectionKey === 'charts' && sectionConfig.charts && (
                                    <div>
                                        <h6 className="text-muted mb-3">Charts:</h6>
                                        <Row>
                                            {Object.entries(sectionConfig.charts).map(([chartKey, chartConfig]) => (
                                                <Col md={6} key={chartKey} className="mb-2">
                                                    <Form.Check
                                                        type="checkbox"
                                                        id={`chart-${chartKey}`}
                                                        label={chartLabels[chartKey]}
                                                        checked={chartConfig.enabled}
                                                        onChange={() => handleChartToggle(chartKey)}
                                                    />
                                                </Col>
                                            ))}
                                        </Row>
                                    </div>
                                )}

                                {/* Boutons d'accès rapide */}
                                {sectionKey === 'quickAccess' && sectionConfig.buttons && (
                                    <div>
                                        <h6 className="text-muted mb-3">Quick Access Buttons:</h6>
                                        <Row>
                                            {Object.entries(sectionConfig.buttons).map(([buttonKey, buttonConfig]) => (
                                                <Col md={6} key={buttonKey} className="mb-2">
                                                    <Form.Check
                                                        type="checkbox"
                                                        id={`button-${buttonKey}`}
                                                        label={buttonLabels[buttonKey]}
                                                        checked={buttonConfig.enabled}
                                                        onChange={() => handleButtonToggle(buttonKey)}
                                                    />
                                                </Col>
                                            ))}
                                        </Row>
                                    </div>
                                )}

                                {/* Composants d'activités */}
                                {sectionKey === 'activities' && sectionConfig.components && (
                                    <div>
                                        <h6 className="text-muted mb-3">Activity Components:</h6>
                                        <Row>
                                            {Object.entries(sectionConfig.components).map(([componentKey, componentConfig]) => (
                                                <Col md={6} key={componentKey} className="mb-2">
                                                    <Form.Check
                                                        type="checkbox"
                                                        id={`component-${componentKey}`}
                                                        label={componentLabels[componentKey]}
                                                        checked={componentConfig.enabled}
                                                        onChange={() => handleComponentToggle(componentKey)}
                                                    />
                                                </Col>
                                            ))}
                                        </Row>
                                    </div>
                                )}
                            </Card.Body>
                        )}
                    </Card>
                ))}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" onClick={resetToDefault}>
                    <i className="fas fa-undo me-1"></i>
                    Reset to Default
                </Button>
                <Button variant="secondary" onClick={onHide}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={saveConfiguration} disabled={saving}>
                    {saving ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                            Saving...
                        </>
                    ) : (
                        <>
                            <i className="fas fa-save me-1"></i>
                            Save Settings
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default DashboardSettings;
